import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../AuthContext';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Fade,
    FormControl,
    Grid,
    Grow,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography
} from '@mui/material';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {
    AccountBalance,
    Assessment,
    AttachMoney,
    People,
    PieChart as PieChartIcon,
    Receipt,
    Timeline,
    TrendingDown,
    TrendingUp
} from '@mui/icons-material';
import axios from 'axios';
import {format, subMonths} from 'date-fns';
import {USER_ENDPOINTS} from '../../Constants';
import CountUp from 'react-countup';
import './Dashboard.css';

// Color palette
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#26c6da'];
const GRADIENT_COLORS = {
    income: ['#00b894', '#00cec9'],
    expenses: ['#ff4757', '#ff6b81'],
    profit: ['#0984e3', '#74b9ff'],
    employees: ['#6c5ce7', '#a29bfe']
};

// Animated Card Component
const AnimatedCard = ({children, delay = 0}) => (
    <Grow in={true} timeout={1000 + delay * 200}>
        <Card sx={{
            height: '100%',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
            },
            borderRadius: '16px'
        }}>
            {children}
        </Card>
    </Grow>
);

const romanianMonths = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
];

const StatCard = ({title, value, icon: Icon, color, trend, delay}) => {
    // Handle NaN, null, undefined values
    const displayValue = isNaN(value) || value === null || value === undefined ? 0 : value;
    const displayTrend = isNaN(trend) || trend === null || trend === undefined ? null : trend;

    return (
        <AnimatedCard delay={delay}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {title}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{color: color, fontWeight: 'bold'}}>
                            <CountUp end={displayValue} duration={2.5} separator="," prefix=""/>
                        </Typography>
                        {displayTrend !== null && (
                            <Box display="flex" alignItems="center" mt={1}>
                                {displayTrend > 0 ? (
                                    <TrendingUp sx={{color: '#00b894', mr: 0.5}}/>
                                ) : (
                                    <TrendingDown sx={{color: '#ff4757', mr: 0.5}}/>
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: displayTrend > 0 ? '#00b894' : '#ff4757',
                                        fontWeight: 'medium'
                                    }}
                                >
                                    {Math.abs(displayTrend)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Icon sx={{
                        fontSize: 48,
                        color: color,
                        opacity: 0.3,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.1)'
                        }
                    }}/>
                </Box>
            </CardContent>
        </AnimatedCard>
    );
};

// Custom tooltip with better styling
const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{
                p: 2,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                borderRadius: '8px'
            }}>
                <Typography variant="subtitle2" sx={{fontWeight: 'bold', mb: 1}}>
                    {label}
                </Typography>
                {payload.map((entry, index) => (
                    <Typography key={index} variant="body2" sx={{color: entry.color}}>
                        {`${entry.name}: ${entry.value.toLocaleString()}`}
                    </Typography>
                ))}
            </Paper>
        );
    }
    return null;
};

function Dashboard() {
    const {isAuthenticated} = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [dashboardData, setDashboardData] = useState({
        incomes: [],
        expenses: [],
        employees: [],
        payrolls: [],
        monthlyData: [],
        statistics: {
            totalIncome: 0,
            totalExpenses: 0,
            totalProfit: 0,
            activeEmployees: 0,
            incomeTrend: 0,
            expenseTrend: 0,
            profitTrend: 0
        }
    });

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
        } else {
            fetchDashboardData();
        }
    }, [isAuthenticated, navigate, selectedYear, selectedMonth]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = {Authorization: `Bearer ${token}`};

            // Fetch all required data
            const [incomesRes, expensesRes, employeesRes, payrollsRes] = await Promise.all([
                axios.get(USER_ENDPOINTS.INBOUND, {
                    params: {month: selectedMonth.toString(), year: selectedYear.toString()},
                    headers
                }),
                axios.get(USER_ENDPOINTS.OUTGOING, {
                    params: {month: selectedMonth.toString(), year: selectedYear.toString()},
                    headers
                }),
                axios.get(USER_ENDPOINTS.EMPLOYEE, {headers}),
                axios.get(USER_ENDPOINTS.PAYROLL, {
                    params: {month: selectedMonth.toString(), year: selectedYear.toString()},
                    headers
                })
            ]);

            // Calculate statistics
            const totalIncome = incomesRes.data.reduce((sum, income) => sum + parseFloat(income.amount || 0), 0);
            const totalExpenses = expensesRes.data.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
            const totalProfit = totalIncome - totalExpenses;
            const activeEmployees = employeesRes.data.filter(emp => emp.isActive).length;

            // Generate monthly trend data (last 6 months)
            const monthlyData = await generateMonthlyTrendData(headers);

            // Calculate trends
            const trends = calculateTrends(monthlyData);

            setDashboardData({
                incomes: incomesRes.data,
                expenses: expensesRes.data,
                employees: employeesRes.data,
                payrolls: payrollsRes.data,
                monthlyData,
                statistics: {
                    totalIncome,
                    totalExpenses,
                    totalProfit,
                    activeEmployees,
                    ...trends
                }
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateMonthlyTrendData = async (headers) => {
        const trends = [];
        for (let i = 5; i >= 0; i--) {
            const date = subMonths(new Date(selectedYear, selectedMonth - 1), i);
            const month = date.getMonth() + 1;
            const year = date.getFullYear();

            const [incomesRes, expensesRes] = await Promise.all([
                axios.get(USER_ENDPOINTS.INBOUND, {
                    params: {month: month.toString(), year: year.toString()},
                    headers
                }),
                axios.get(USER_ENDPOINTS.OUTGOING, {
                    params: {month: month.toString(), year: year.toString()},
                    headers
                })
            ]);

            const income = incomesRes.data.reduce((sum, item) => sum + parseFloat(item.amount), 0);
            const expenses = expensesRes.data.reduce((sum, item) => sum + parseFloat(item.amount), 0);

            trends.push({
                name: format(date, 'MMM yyyy'),
                income,
                expenses,
                profit: income - expenses
            });
        }
        return trends;
    };

    const calculateTrends = (monthlyData) => {
        if (monthlyData.length < 2) return {incomeTrend: 0, expenseTrend: 0, profitTrend: 0};

        const latest = monthlyData[monthlyData.length - 1];
        const previous = monthlyData[monthlyData.length - 2];

        const incomeTrend = ((latest.income - previous.income) / previous.income) * 100;
        const expenseTrend = ((latest.expenses - previous.expenses) / previous.expenses) * 100;
        const profitTrend = ((latest.profit - previous.profit) / previous.profit) * 100;

        return {
            incomeTrend: Math.round(incomeTrend * 10) / 10,
            expenseTrend: Math.round(expenseTrend * 10) / 10,
            profitTrend: Math.round(profitTrend * 10) / 10
        };
    };

    // Prepare data for charts
    const paymentMethodData = dashboardData.incomes.reduce((acc, income) => {
        const existing = acc.find(item => item.name === income.transactionType);
        if (existing) {
            existing.value += parseFloat(income.amount);
        } else {
            acc.push({name: income.transactionType, value: parseFloat(income.amount)});
        }
        return acc;
    }, []);

    const expenseTypeData = dashboardData.expenses.reduce((acc, expense) => {
        const existing = acc.find(item => item.name === expense.paymentType);
        if (existing) {
            existing.value += parseFloat(expense.amount);
        } else {
            acc.push({name: expense.paymentType, value: parseFloat(expense.amount)});
        }
        return acc;
    }, []);

    const employeeStatusData = [
        {name: 'Active', value: dashboardData.employees.filter(emp => emp.isActive).length},
        {name: 'Inactive', value: dashboardData.employees.filter(emp => !emp.isActive).length}
    ];

    const salaryDistributionData = dashboardData.employees.map(emp => ({
        name: emp.name,
        salary: parseFloat(emp.salary)
    })).sort((a, b) => b.salary - a.salary);

    if (loading) {
        return (
            <AuthenticatedLayout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress/>
                </Box>
            </AuthenticatedLayout>
        );
    }

    return (
        <AuthenticatedLayout>
            <Box sx={{flexGrow: 1}}>
                <Fade in={true} timeout={800}>
                    <Box className="box" mb={4}>
                        <h1 className="page-title">Dashboard</h1>
                        <Box display="flex" gap={2}>
                            <FormControl size="small" sx={{minWidth: 120}}>
                                <InputLabel>Lună</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    label="Lună"
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                    {romanianMonths.map((monthName, index) => (
                                        <MenuItem key={index + 1} value={index + 1}>
                                            {monthName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{minWidth: 120}}>
                                <InputLabel>An</InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="An"
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    {Array.from({length: 5}, (_, i) => (
                                        <MenuItem key={i} value={new Date().getFullYear() - 2 + i}>
                                            {new Date().getFullYear() - 2 + i}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Fade>

                {/* Stat Cards Row */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Venituri"
                            value={dashboardData.statistics.totalIncome}
                            icon={AttachMoney}
                            color="#00b894"
                            trend={dashboardData.statistics.incomeTrend}
                            delay={0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Total Cheltuieli"
                            value={dashboardData.statistics.totalExpenses}
                            icon={Receipt}
                            color="#ff4757"
                            trend={dashboardData.statistics.expenseTrend}
                            delay={1}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Profit Net"
                            value={dashboardData.statistics.totalProfit}
                            icon={AccountBalance}
                            color="#0984e3"
                            trend={dashboardData.statistics.profitTrend}
                            delay={2}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard
                            title="Angajați Activi"
                            value={dashboardData.statistics.activeEmployees}
                            icon={People}
                            color="#6c5ce7"
                            delay={3}
                        />
                    </Grid>
                </Grid>

                {/* Charts Row 1 */}
                <Grid container spacing={3} mb={4} class="grid-content">
                    <Grid item xs={12} md={8} className="table-content">
                        <AnimatedCard delay={4}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Timeline sx={{mr: 1, color: '#0984e3'}}/>
                                    Trend P&L (Ultimele 6 luni)
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <AreaChart data={dashboardData.monthlyData}>
                                        <defs>
                                            <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#00b894" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#00b894" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ff4757" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#ff4757" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#0984e3" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#0984e3" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                        <XAxis dataKey="name"/>
                                        <YAxis/>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        <Legend/>
                                        <Area
                                            type="monotone"
                                            dataKey="income"
                                            stroke="#00b894"
                                            fillOpacity={1}
                                            fill="url(#colorIncome)"
                                            name="Venituri"
                                            strokeWidth={3}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="expenses"
                                            stroke="#ff4757"
                                            fillOpacity={1}
                                            fill="url(#colorExpenses)"
                                            name="Cheltuieli"
                                            strokeWidth={3}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="profit"
                                            stroke="#0984e3"
                                            fillOpacity={1}
                                            fill="url(#colorProfit)"
                                            name="Profit"
                                            strokeWidth={3}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                    <Grid item xs={12} md={4} className="table-content">
                        <AnimatedCard delay={5}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <PieChartIcon sx={{mr: 1, color: '#26c6da'}}/>
                                    Metode de Plată
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <PieChart>
                                        <Pie
                                            data={paymentMethodData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            dataKey="value"
                                            animationBegin={0}
                                            animationDuration={1500}
                                        >
                                            {paymentMethodData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip/>}/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                </Grid>

                {/* Charts Row 2 */}
                <Grid container spacing={3} mb={4} class="grid-content">
                    <Grid item xs={12} md={6} class="table-content">
                        <AnimatedCard delay={6}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <People sx={{mr: 1, color: '#6c5ce7'}}/>
                                    Top 10 Salarii Angajați
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart
                                        data={salaryDistributionData.slice(0, 10)}
                                        layout="vertical"
                                        margin={{top: 5, right: 30, left: 50, bottom: 5}}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                        <XAxis type="number"/>
                                        <YAxis dataKey="name" type="category" width={100}/>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        <Bar dataKey="salary" fill="#6c5ce7" animationDuration={1500}>
                                            {salaryDistributionData.slice(0, 10).map((entry, index) => (
                                                <Cell key={`cell-${index}`}
                                                      fill={`rgba(108, 92, 231, ${1 - index * 0.08})`}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                    <Grid item xs={12} md={6} className="table-content">
                        <AnimatedCard delay={7}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Assessment sx={{mr: 1, color: '#ff4757'}}/>
                                    Tipuri de Cheltuieli
                                </Typography>
                                <ResponsiveContainer width="100%" height={400}>
                                    <PieChart>
                                        <Pie
                                            data={expenseTypeData}
                                            cx="50%"
                                            cy="50%"
                                            startAngle={180}
                                            endAngle={0}
                                            innerRadius={80}
                                            outerRadius={150}
                                            fill="#8884d8"
                                            paddingAngle={5}
                                            dataKey="value"
                                            animationBegin={0}
                                            animationDuration={1500}
                                        >
                                            {expenseTypeData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        <Legend verticalAlign="bottom" height={36}/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                </Grid>

                {/* Employee Status and Summary */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4} className="grid-content">
                        <AnimatedCard delay={8}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <People sx={{mr: 1, color: '#26c6da'}}/>
                                    Status Angajați
                                </Typography>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={employeeStatusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            paddingAngle={5}
                                            dataKey="value"
                                            animationBegin={0}
                                            animationDuration={1500}
                                        >
                                            {employeeStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={index === 0 ? '#00b894' : '#ff4757'}/>
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        <Legend/>
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                    <Grid item xs={12} md={8} className="grid-content">
                        <AnimatedCard delay={9}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Timeline sx={{mr: 1, color: '#ffa502'}}/>
                                    Sumar Financiar
                                </Typography>
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                                    gap={3}
                                    sx={{mt: 2}}
                                >
                                    {[
                                        {
                                            label: 'Rata Profitabilitate',
                                            value: `${Math.round((dashboardData.statistics.totalProfit / dashboardData.statistics.totalIncome) * 100)}%`,
                                            color: dashboardData.statistics.totalProfit > 0 ? '#00b894' : '#ff4757'
                                        },
                                        {
                                            label: 'Cost per Angajat',
                                            value: `${Math.round(dashboardData.payrolls.reduce((sum, p) => sum + parseFloat(p.totalSalary), 0) / dashboardData.statistics.activeEmployees)}`,
                                            color: '#6c5ce7'
                                        },
                                        {
                                            label: 'Venit per Angajat',
                                            value: `${Math.round(dashboardData.statistics.totalIncome / dashboardData.statistics.activeEmployees)}`,
                                            color: '#00b894'
                                        },
                                        {
                                            label: 'Cheltuieli Operaționale',
                                            value: `${Math.round((dashboardData.statistics.totalExpenses / dashboardData.statistics.totalIncome) * 100)}%`,
                                            color: '#ff4757'
                                        }
                                    ].map((item, index) => (
                                        <Paper
                                            key={index}
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                background: `linear-gradient(135deg, ${item.color}10, ${item.color}05)`,
                                                borderRadius: '12px',
                                                border: `1px solid ${item.color}20`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-5px)',
                                                    boxShadow: `0 8px 24px ${item.color}15`
                                                }
                                            }}
                                        >
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="h5" sx={{color: item.color, fontWeight: 'bold'}}>
                                                {item.value}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            </CardContent>
                        </AnimatedCard>
                    </Grid>
                </Grid>
            </Box>
        </AuthenticatedLayout>
    );
}

export default Dashboard;